/**
 * Accessibility Provider
 * Provides comprehensive accessibility features including screen reader support,
 * keyboard navigation, high contrast mode, dynamic text scaling, voice control,
 * and focus management
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { AccessibilityInfo, Platform } from 'react-native';
import { useTheme } from '../themes/ThemeProvider';
import { useAccessibility } from '../hooks/useAccessibility';
import {
  getHighContrastColors,
  validateColorContrast,
  ACCESSIBILITY_CONSTANTS,
  type HighContrastColors,
} from '../utils/accessibility';

// Accessibility context types
export interface AccessibilityContextValue {
  // State
  isScreenReaderEnabled: boolean;
  isHighContrastEnabled: boolean;
  isDynamicTextEnabled: boolean;
  isVoiceControlEnabled: boolean;
  isReduceMotionEnabled: boolean;
  textScale: number;
  userScale: number;
  
  // Utilities
  announce: (message: string) => void;
  announceLoading: (message?: string) => void;
  announceCompletion: (message: string) => void;
  announceError: (message: string) => void;
  announceSuccess: (message: string) => void;
  
  // Label and hint generation
  generateLabel: (element: string, action?: string, state?: string, context?: string) => string;
  generateHint: (action: string, result?: string, context?: string) => string;
  generateVoiceHint: (action: string, element: string, state?: string) => string;
  
  // High contrast utilities
  getHighContrastColors: (baseColors: {
    foreground: string;
    background: string;
    border?: string;
    accent?: string;
  }) => HighContrastColors;
  
  // Dynamic text scaling
  getScaledText: (baseSize: number, maxScale?: number) => number;
  setTextScale: (scale: number) => void;
  
  // Validation utilities
  validateTouchTarget: (width: number, height: number, minSize?: number) => {
    isValid: boolean;
    issues: string[];
  };
  validateColorContrast: (foreground: string, background: string, level?: 'AA' | 'AAA') => {
    isValid: boolean;
    ratio: number;
    level: 'AA' | 'AAA' | 'fail';
  };
  
  // Focus management
  registerFocusable: (id: string, ref: React.RefObject<any>, onFocus?: () => void, onBlur?: () => void) => void;
  unregisterFocusable: (id: string) => void;
  focusElement: (id: string) => boolean;
  focusNext: () => boolean;
  focusPrevious: () => boolean;
  focusFirst: () => boolean;
  focusLast: () => boolean;
  getCurrentFocus: () => string | null;
  clearFocus: () => void;
  
  // Constants
  constants: typeof ACCESSIBILITY_CONSTANTS;
  
  // Theme integration
  getAccessibleColors: (colorKey: string) => {
    foreground: string;
    background: string;
    border: string;
    accent: string;
  };
  validateThemeAccessibility: () => {
    isValid: boolean;
    issues: string[];
    warnings: string[];
  };
}

// Create accessibility context
const AccessibilityContext = createContext<AccessibilityContextValue | null>(null);

/**
 * Accessibility Provider Props
 */
interface AccessibilityProviderProps {
  children: React.ReactNode;
  enableHighContrast?: boolean;
  enableDynamicText?: boolean;
  enableVoiceControl?: boolean;
  enableFocusManagement?: boolean;
  validateOnMount?: boolean;
  onAccessibilityChange?: (state: Partial<AccessibilityContextValue>) => void;
}

/**
 * Accessibility Provider Component
 */
export function AccessibilityProvider({
  children,
  enableHighContrast = true,
  enableDynamicText = true,
  enableVoiceControl = true,
  enableFocusManagement = true,
  validateOnMount = true,
  onAccessibilityChange,
}: AccessibilityProviderProps) {
  const { theme } = useTheme();
  const accessibility = useAccessibility();
  
  // State for theme integration
  const [themeValidation, setThemeValidation] = useState<{
    isValid: boolean;
    issues: string[];
    warnings: string[];
  }>({
    isValid: true,
    issues: [],
    warnings: [],
  });

  // Validate theme accessibility on mount and theme changes
  useEffect(() => {
    if (validateOnMount) {
      const validation = validateThemeAccessibility();
      setThemeValidation(validation);
      
      if (!validation.isValid) {
        console.warn('Theme accessibility validation failed:', validation.issues);
      }
      if (validation.warnings.length > 0) {
        console.warn('Theme accessibility warnings:', validation.warnings);
      }
    }
  }, [theme, validateOnMount]);

  // Notify parent of accessibility changes
  useEffect(() => {
    onAccessibilityChange?.(accessibility);
  }, [accessibility, onAccessibilityChange]);

  /**
   * Get accessible colors for a given color key
   */
  const getAccessibleColors = (colorKey: string) => {
    const themeColors = theme.colors;
    
    // Map color keys to theme colors
    const colorMap: Record<string, { foreground: string; background: string; border?: string; accent?: string }> = {
      primary: {
        foreground: themeColors.text.inverse,
        background: themeColors.primary[500],
        border: themeColors.primary[500],
        accent: themeColors.primary[600],
      },
      secondary: {
        foreground: themeColors.text.primary,
        background: themeColors.surface.secondary,
        border: themeColors.border.primary,
        accent: themeColors.primary[200],
      },
      surface: {
        foreground: themeColors.text.primary,
        background: themeColors.surface.primary,
        border: themeColors.border.primary,
        accent: themeColors.primary[200],
      },
      error: {
        foreground: themeColors.text.inverse,
        background: themeColors.semantic.error.DEFAULT,
        border: themeColors.semantic.error.DEFAULT,
        accent: themeColors.semantic.error.hover,
      },
      success: {
        foreground: themeColors.text.inverse,
        background: themeColors.semantic.success.DEFAULT,
        border: themeColors.semantic.success.DEFAULT,
        accent: themeColors.semantic.success.hover,
      },
      warning: {
        foreground: themeColors.text.primary,
        background: themeColors.semantic.warning.DEFAULT,
        border: themeColors.semantic.warning.DEFAULT,
        accent: themeColors.semantic.warning.hover,
      },
    };

    const baseColors = colorMap[colorKey] || {
      foreground: themeColors.text.primary,
      background: themeColors.surface.primary,
      border: themeColors.border.primary,
      accent: themeColors.primary[500],
    };

    return accessibility.getHighContrastColors(baseColors);
  };

  /**
   * Validate theme accessibility
   */
  const validateThemeAccessibility = () => {
    const issues: string[] = [];
    const warnings: string[] = [];
    const themeColors = theme.colors;

    // Validate primary color contrast
    const primaryContrast = validateColorContrast(
      themeColors.text.inverse,
      themeColors.primary[500],
      'AA'
    );
    if (!primaryContrast.isValid) {
      issues.push(`Primary button contrast ratio ${primaryContrast.ratio.toFixed(2)} does not meet AA standards`);
    }

    // Validate text contrast
    const textContrast = validateColorContrast(
      themeColors.text.primary,
      themeColors.surface.primary,
      'AA'
    );
    if (!textContrast.isValid) {
      issues.push(`Text contrast ratio ${textContrast.ratio.toFixed(2)} does not meet AA standards`);
    }

    // Validate secondary text contrast
    const secondaryTextContrast = validateColorContrast(
      themeColors.text.secondary,
      themeColors.surface.primary,
      'AA'
    );
    if (!secondaryTextContrast.isValid) {
      issues.push(`Secondary text contrast ratio ${secondaryTextContrast.ratio.toFixed(2)} does not meet AA standards`);
    }

    // Validate border contrast
    const borderContrast = validateColorContrast(
      themeColors.border.primary,
      themeColors.surface.primary,
      'AA'
    );
    if (!borderContrast.isValid) {
      warnings.push(`Border contrast ratio ${borderContrast.ratio.toFixed(2)} is below recommended AA standards`);
    }

    // Validate error color contrast
    const errorContrast = validateColorContrast(
      themeColors.text.inverse,
      themeColors.semantic.error.DEFAULT,
      'AA'
    );
    if (!errorContrast.isValid) {
      issues.push(`Error color contrast ratio ${errorContrast.ratio.toFixed(2)} does not meet AA standards`);
    }

    // Validate success color contrast
    const successContrast = validateColorContrast(
      themeColors.text.inverse,
      themeColors.semantic.success.DEFAULT,
      'AA'
    );
    if (!successContrast.isValid) {
      issues.push(`Success color contrast ratio ${successContrast.ratio.toFixed(2)} does not meet AA standards`);
    }

    return {
      isValid: issues.length === 0,
      issues,
      warnings,
    };
  };

  // Context value
  const contextValue: AccessibilityContextValue = {
    ...accessibility,
    getAccessibleColors,
    validateThemeAccessibility,
  };

  return (
    <AccessibilityContext.Provider value={contextValue}>
      {children}
    </AccessibilityContext.Provider>
  );
}

/**
 * Hook to use accessibility context
 */
export function useAccessibilityContext(): AccessibilityContextValue {
  const context = useContext(AccessibilityContext);

  if (!context) {
    throw new Error('useAccessibilityContext must be used within an AccessibilityProvider');
  }

  return context;
}

/**
 * Hook for screen reader specific functionality
 */
export function useScreenReaderContext() {
  const context = useAccessibilityContext();
  
  return {
    isEnabled: context.isScreenReaderEnabled,
    announce: context.announce,
    announceLoading: context.announceLoading,
    announceCompletion: context.announceCompletion,
    announceError: context.announceError,
    announceSuccess: context.announceSuccess,
    generateLabel: context.generateLabel,
    generateHint: context.generateHint,
    generateVoiceHint: context.generateVoiceHint,
  };
}

/**
 * Hook for high contrast mode
 */
export function useHighContrastContext() {
  const context = useAccessibilityContext();
  
  return {
    isEnabled: context.isHighContrastEnabled,
    getColors: context.getHighContrastColors,
    getAccessibleColors: context.getAccessibleColors,
  };
}

/**
 * Hook for dynamic text scaling
 */
export function useDynamicTextContext() {
  const context = useAccessibilityContext();
  
  return {
    isEnabled: context.isDynamicTextEnabled,
    currentScale: context.textScale,
    userScale: context.userScale,
    getScaledText: context.getScaledText,
    setTextScale: context.setTextScale,
    canScaleUp: context.userScale < 2.0,
    canScaleDown: context.userScale > 0.8,
  };
}

/**
 * Hook for focus management
 */
export function useFocusManagementContext() {
  const context = useAccessibilityContext();
  
  return {
    register: context.registerFocusable,
    unregister: context.unregisterFocusable,
    focus: context.focusElement,
    next: context.focusNext,
    previous: context.focusPrevious,
    first: context.focusFirst,
    last: context.focusLast,
    getCurrent: context.getCurrentFocus,
    clear: context.clearFocus,
  };
}

/**
 * Hook for accessibility validation
 */
export function useAccessibilityValidation() {
  const context = useAccessibilityContext();
  
  return {
    validateTouchTarget: context.validateTouchTarget,
    validateColorContrast: context.validateColorContrast,
    validateThemeAccessibility: context.validateThemeAccessibility,
    constants: context.constants,
  };
} 